<section class="admin-section" data-test="nudger-editor">
  <h2 class="admin-h2">Nudger rules</h2>
  <p class="admin-lede">
    The reminders the tutor nudger sends when attendance, a session report or a progress report is missing.
    A saved change applies from the nudger's next run. Turning a rule off stops new nudges; nudges it has
    already raised are unaffected. How far back the nudger looks, and how long a sent nudge is not repeated,
    are on the Settings tab.
  </p>

  @if (error) {
    <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" data-test="nudger-error">{{error}}</div>
  }

  @if (loading) {
    <p>Loading…</p>
  }

  @if (!loading) {
    @for (rule of rules; track rule.workFlowType) {
      <div class="admin-panel" [class.tw:opacity-70]="!isEnabled(rule)"
        [attr.data-test]="'nudger-rule-' + rule.workFlowType">
        <div class="tw:flex tw:flex-wrap tw:items-start tw:gap-4">
          <div class="tw:flex-1 tw:min-w-64">
            <!-- admin-h3 (admin-page.css): the global h3 style is 28px, far louder than the 14px body around it. -->
            <h3 class="admin-h3">
              {{rule.name}}
              <span class="tw:text-xs tw:font-normal tw:ml-2 tw:px-2 tw:py-0.5 tw:rounded tw:bg-gray-100 tw:align-middle" data-test="nudger-channel">{{rule.channel}}</span>
            </h3>
            <div class="tw:text-sm tw:opacity-75">{{rule.description}}</div>
            <div class="tw:text-xs tw:opacity-50">Rule {{rule.workFlowType}}</div>
          </div>
          <label class="field__toggle" title="Rule on or off">
            <input type="checkbox" [checked]="isEnabled(rule)" [disabled]="saving === rule.workFlowType"
              (change)="setEnabled(rule, $any($event.target).checked)" data-test="nudger-enabled" />
            <span class="field__toggle-track"></span>
            <span class="tw:ml-2 tw:text-sm">{{isEnabled(rule) ? 'On' : 'Off'}}</span>
          </label>
        </div>

        @if (!rule.parametersReadable) {
          <div class="tw:my-2 tw:p-2 tw:border tw:border-amber-500 tw:bg-amber-50 tw:text-amber-900 tw:rounded tw:text-sm" data-test="nudger-unreadable">
            The stored settings for this rule could not be read. Fill in every field and save to replace them.
          </div>
        }

        <div class="tw:grid tw:gap-4 tw:mt-3 tw:grid-cols-1 tw:md:grid-cols-[repeat(auto-fill,minmax(16rem,20rem))]">
          @for (field of rule.fields; track field.key) {
            <div [attr.data-test]="'nudger-field-' + field.key">
              <label class="field__label" [attr.for]="inputId(rule, field)">{{field.label}}</label>
              @if (field.kind === 'provision') {
                <select class="field__control" [id]="inputId(rule, field)" [value]="value(rule, field)"
                  [disabled]="saving === rule.workFlowType" (change)="edit(rule, field, $any($event.target).value)"
                  data-test="nudger-provision">
                  @if (!value(rule, field)) {
                    <option value="" selected>Choose…</option>
                  }
                  @for (option of provisionValues; track option) {
                    <option [value]="option" [selected]="value(rule, field) === option">{{provisionLabels[option]}}</option>
                  }
                </select>
              } @else if (field.kind === 'time') {
                <input type="time" class="field__control" [id]="inputId(rule, field)" [value]="value(rule, field)"
                  [disabled]="saving === rule.workFlowType" (input)="edit(rule, field, $any($event.target).value)"
                  data-test="nudger-time" />
              } @else {
                <div class="tw:flex tw:items-center tw:gap-2">
                  <input type="number" class="field__control" step="1" [id]="inputId(rule, field)"
                    [attr.min]="field.min" [attr.max]="field.max" [value]="value(rule, field)"
                    [disabled]="saving === rule.workFlowType" (input)="edit(rule, field, $any($event.target).value)"
                    data-test="nudger-int" />
                  @if (field.unit) {
                    <span class="tw:text-sm tw:opacity-75">{{field.unit}}</span>
                  }
                </div>
                @if (field.min != null && field.max != null) {
                  <div class="tw:text-xs tw:opacity-60">{{field.min}} to {{field.max}}</div>
                }
              }
              @if (field.help) {
                <div class="tw:text-xs tw:opacity-75">{{field.help}}</div>
              }
              @if (validate(rule, field)) {
                <div class="tw:text-sm tw:text-red-700" data-test="nudger-invalid">{{validate(rule, field)}}</div>
              }
            </div>
          }
        </div>

        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-4">
          <button type="button" class="btn" [disabled]="!canSave(rule)" (click)="save(rule)" data-test="nudger-save">Save</button>
          <button type="button" class="btn" [disabled]="!isDirty(rule) || saving === rule.workFlowType" (click)="reset(rule)" data-test="nudger-reset">Undo changes</button>
          <button type="button" class="btn" (click)="history(rule)" data-test="nudger-history">History</button>
          <span class="tw:text-sm tw:opacity-75 tw:ml-auto" data-test="nudger-last-changed">
            @if (rule.updatedUtc) {
              Last changed by <admin-email [value]="rule.updatedBy || 'unknown'" /> on {{rule.updatedUtc | date:'dd/MM/yyyy HH:mm'}}
            } @else {
              Never changed
            }
          </span>
        </div>
      </div>
    }
  }

  @if (!loading && !error && rules.length === 0) {
    <p class="tw:opacity-60" data-test="nudger-empty">No nudger rules are configured.</p>
  }
</section>
